import { lazy, Suspense, useEffect, createContext, useReducer, Fragment } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import classNames from 'classnames';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocalStorage } from 'hooks';

// config
import { siteUrl } from 'config/constants';

// components
import Navbar from 'components/Navbar';
import ThemeProvider from 'components/ThemeProvider';
import { tokens } from 'components/ThemeProvider/theme';
import VisuallyHidden from 'components/VisuallyHidden';
import SEO from 'components/SEO';
import PWAInstallPrompt from 'components/PWAInstallPrompt';

// utils
import { msToNum } from 'utils/style';
import { reflow } from 'utils/transition';
import prerender from 'utils/prerender';
import { initializeServiceWorker } from 'utils/serviceWorkerRegistration';
import { initializePerformanceMonitoring } from 'utils/performanceMetrics';

import { initialState, reducer } from 'app/reducer';

import './reset.css';
import './index.css';

// Phase 4 testing utilities (development only)
if (process.env.NODE_ENV === 'development') {
  import('utils/phase4Test').then(module => {
    window.phase4Tests = module.default;
  });
  import('utils/performanceBaseline').then(module => {
    window.performanceBaseline = module.default;
  });
  import('utils/buildOptimizations').then(module => {
    window.buildOptimizations = module.default;
  });
  import('utils/seoOptimization').then(module => {
    window.seoOptimization = module.default;
    window.generateSEOReport = module.generateSEOReport;
    window.generateXMLSitemap = module.generateXMLSitemap;
    window.analyzeSEO = module.analyzeSEO;
  });
}

const Home = lazy(() => import('pages/Home'));
const Contact = lazy(() => import('pages/Contact'));
const Page404 = lazy(() => import('pages/404'));

export const AppContext = createContext();
export const TransitionContext = createContext();

const repoPrompt = `
__  __  __
Taking a peek huh? Check out the source code: https://github.com/hashaneranda/portfolio-2021
`;

const App = () => {
  const [storedTheme] = useLocalStorage('theme', 'dark');
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!prerender) {
      console.info(`${repoPrompt}\n\n`);

      // Initialize performance monitoring
      initializePerformanceMonitoring();
    }
    // Enable back/forward cache by using automatic scroll restoration
    window.history.scrollRestoration = 'auto';

    // Initialize service worker and PWA features
    if (!prerender && process.env.NODE_ENV === 'production') {
      initializeServiceWorker({
        enablePreloading: true,
        enableBackgroundSync: true,
        onSuccess: registration => {
          console.log('Enhanced Service Worker registered successfully');

          // Dispatch service worker success event
          dispatch({
            type: 'setServiceWorkerStatus',
            value: 'registered',
          });
        },
        onUpdate: registration => {
          console.log('Service Worker updated - new content available');

          // Dispatch service worker update event
          dispatch({
            type: 'setServiceWorkerStatus',
            value: 'updated',
          });
        },
        onOffline: () => {
          console.log('App is offline - enabling offline mode');

          // Dispatch offline event
          dispatch({
            type: 'setNetworkStatus',
            value: 'offline',
          });
        },
        onOnline: () => {
          console.log('App is online - syncing data');

          // Dispatch online event
          dispatch({
            type: 'setNetworkStatus',
            value: 'online',
          });
        },
      }).catch(error => {
        console.error('Service Worker registration failed:', error);

        // Dispatch service worker error event
        dispatch({
          type: 'setServiceWorkerStatus',
          value: 'error',
        });
      });
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch({ type: 'setTheme', value: storedTheme });
  }, [storedTheme]);

  return (
    <HelmetProvider>
      <AppContext.Provider value={{ ...state, dispatch }}>
        <ThemeProvider themeId={state.theme}>
          <BrowserRouter>
            <SEO />
            <AppRoutes />
            {/* PWA Install Prompt */}
            <PWAInstallPrompt
              showOnMobile={true}
              showOnDesktop={true}
              autoHide={true}
              hideDelay={10000}
              onInstallSuccess={() => {
                console.log('PWA installed successfully');
                dispatch({
                  type: 'setPWAStatus',
                  value: 'installed',
                });
              }}
              onInstallCancel={() => {
                console.log('PWA install cancelled');
              }}
              onInstallError={error => {
                console.error('PWA install failed:', error);
              }}
            />
          </BrowserRouter>
        </ThemeProvider>
      </AppContext.Provider>
    </HelmetProvider>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const { pathname } = location;

  return (
    <Fragment>
      <Helmet>
        <link rel="canonical" href={`${siteUrl}${pathname}`} />
      </Helmet>
      <VisuallyHidden showOnFocus as="a" className="skip-to-main" href="#MainContent">
        Skip to main content
      </VisuallyHidden>
      <Navbar location={location} />
      <TransitionGroup component="main" className="app" tabIndex={-1} id="MainContent">
        <Transition
          key={pathname}
          timeout={msToNum(tokens.base.durationS)}
          onEnter={reflow}
        >
          {status => (
            <TransitionContext.Provider value={{ status }}>
              <div className={classNames('app__page', `app__page--${status}`)}>
                <Suspense fallback={<Fragment />}>
                  <Routes location={location}>
                    <Route path="/" element={<Home />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="*" element={<Page404 />} />
                  </Routes>
                </Suspense>
              </div>
            </TransitionContext.Provider>
          )}
        </Transition>
      </TransitionGroup>
    </Fragment>
  );
};

export default App;
